import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BackendRequest, BackendRequestMethods, JsonOf } from '../utils/types/backendTypes';
import { EventQueueService } from './event-queue-service.service';
import {isKind} from "../models/IKind.interface";
import {isEltern} from "../models/IEltern.interface";
import {isAktion} from "../models/IAktion.interface";
import {isAnmeldung} from "../models/IAnmeldung.interface";
import {isAusgaben} from "../models/IAusgaben.interface";
import {isIcon} from "../models/IIcon.interface";

@Injectable({
  providedIn: 'root'
})
export class BackendService {

  private dbRevision = -1;

  constructor(private http: HttpClient,
    private eventQueue: EventQueueService) {

    setInterval(() => {
      // noinspection JSIgnoredPromiseFromCall
      this.updateDbRevision();
    }, 3000);
  }

  public static replaceDate(obj: any, propName: string) {
    if (typeof obj[propName] !== 'number') {
      throw new Error(propName + ' is not a Number: ' + obj[propName]);
    }
    obj[propName] = new Date(obj[propName]);
  }

  public static updateIdToProperty(obj: any, propName: string, getFn: (id: number) => any, isArray: boolean, sourceDataRevisionGetter:()=>number) {
    if (isArray) {
      let rev = null;
      const elemIds = obj[propName];
      const elemCache = [];
      delete obj[propName];
      Object.defineProperty(obj, propName, {
        get: () => {
          const newRevision = sourceDataRevisionGetter();
          if (rev !== newRevision) {
            elemCache.length = 0;
            elemIds.forEach(elemId => {
              elemCache.push(getFn(elemId));
            });
            rev = newRevision;
          }
          return elemCache;
        },
        enumerable: true
      });
    } else {
      const elemId = obj[propName];
      const cache = { data: undefined, rev: null };
      delete obj[propName];
      Object.defineProperty(obj, propName, {
        get: () => {
          const newRevision = sourceDataRevisionGetter();
          //console.log(propName);
          if (!cache.data || cache.rev !== newRevision) {
            cache.data = getFn(elemId);
            cache.rev = newRevision;
          }
          return cache.data;
        },
        set: val => {
          cache.data = val;
        },
        enumerable: true
      });
    }
  }

  public static toJsonField<T>(obj: T): JsonOf<T> {
    if (obj === null) {
      return null;
    }
    if (obj === undefined) {
      return "" as unknown as any;
    }
    if (typeof obj === 'boolean' || typeof obj === 'number' || typeof obj === 'string') {
      return obj as unknown as any;
    }
    if (typeof obj['getTime'] === 'function') {// Es ist ein Date
      return obj['getTime']();
    }
    //@ts-ignore
    if (obj.id !== undefined) {
      //@ts-ignore
      return obj.id as unknown as any;
    }
    return obj as unknown as any;
  }

  public static toSearchJsonField<T>(obj: T): JsonOf<T> {
    if (obj === null) {
      return null;
    }
    if (obj === undefined) {
      return null;
    }
    if (typeof obj === 'boolean' || typeof obj === 'number' || typeof obj === 'string') {
      return obj as unknown as any;
    }
    if (typeof obj['getTime'] === 'function') {// Es ist ein Date
      return obj['getTime']();
    }
    if (isAktion(obj)) {
      return obj.id + " " + obj.titel + " " + obj.veranstalter + " " + obj.ort as unknown as any;
    }
    if (isAnmeldung(obj)) {
      return obj.id + " " + obj.kind.vorname + " " + obj.kind.nachname + " " + obj.aktion.titel + " " + obj.aktion.veranstalter as unknown as any;
    }
    if (isAusgaben(obj)) {
      return obj.id + " " + obj.beschreibung + " " + obj.preis as unknown as any;
    }
    if (isIcon(obj)) {
      return obj.id as unknown as any;
    }
    if (isKind(obj) || isEltern(obj)) {
      return obj.id + " " + obj.vorname + " " + obj.nachname as unknown as any;
    }
    return obj as unknown as any;
  }

  public static toJsonObj<T>(obj: T): JsonOf<T> {
    if (Array.isArray(obj)) {
      return obj.map(BackendService.toJsonObj) as unknown as JsonOf<T>;
    }
    const newObj: any = {};

    // tslint:disable-next-line: forin
    for (const key in obj) {
      const val = obj[key];
      if (Array.isArray(val)) {
        newObj[key] = val.map(BackendService.toJsonField);
      } else {
        newObj[key] = BackendService.toJsonField(val);
      }
    }

    return newObj;
  }

  public static toSearchJsonObj<T>(obj: T): JsonOf<T> {
    if (Array.isArray(obj)) {
      return obj.map(BackendService.toSearchJsonObj) as unknown as JsonOf<T>;
    }

    const newObj: any = {};
    // tslint:disable-next-line: forin
    for (const key in obj) {
      const val = obj[key];
      if (key === "icon" || key === "passworthash") {
        newObj[key] = "";
      } else if (Array.isArray(val)) {
        newObj[key] = val.map(BackendService.toSearchJsonField).join(" ");
      } else {
        newObj[key] = BackendService.toSearchJsonField(val);
      }
    }

    return newObj;
  }

  private toXml(key: string, value: string | boolean | number | undefined | Object | any[]) {
    if (Array.isArray(value)) {
      return this.arrayToXml(key, value);
    }
    if (typeof value === 'object') {
      return `<${key} xmlns="">${this.objectToXml(value)}</${key}>\n`;
    } else if (typeof value === 'string') {
      return `<${key} xmlns="">${BackendService.escapeXml(value)}</${key}>\n`;
    }
    return `<${key} xmlns="">${value}</${key}>\n`;
  }

  private static escapeXml(unsafe: string): string {
    return unsafe.replace(/[<>&'"]/g, (c) => {
      switch (c) {
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '&': return '&amp;';
        case '\'': return '&apos;';
        case '"': return '&quot;';
      }
    });
  }

  private static restoreXml(save: string): string {
    return save.replace(/&lt;|&gt;|&amp;|&apos;|&quot;/g, (c) => {
      switch (c) {
        case '&lt;': return '<';
        case '&gt;': return '>';
        case '&amp;': return '&';
        case '&quot;': return '"';
        case '&apos;': return '\'';
      }
    });
  }

  private arrayToXml(key: string, value: any[]) {
    let result = '';
    value.forEach((v) => {
      if (Array.isArray(v)) {
        result += `<${key} xmlns="">${this.arrayToXml(key, v)}</${key}>\n`;
      } else {
        result += this.toXml(key, v);
      }
    });
    return result;
  }

  private objectToXml(value: Object) {
    let result = '';

    if (value === null) {
      return null;
    }

    for (const key in value) {
      if (value.hasOwnProperty(key)) {
        result += this.toXml(key, value[key]);
      }
    }

    return result;
  }

  public async sendRequest<Method extends BackendRequestMethods, Request extends BackendRequest<Method>>(
    method: Method,
    params: Request['params'],
    triggerReload: Request['triggerReload']): Promise<Request['returnType']> {
    console.debug('Send ' + method + ' Request: ', params);

    let data;
    try {
      let paramsString = '';
      for (const key in params) {
        if (params.hasOwnProperty(key)) {
          paramsString += this.toXml(key, params[key]);
        }
      }

      data = await this.http.post('/service',
        `<Envelope xmlns="http://schemas.xmlsoap.org/soap/envelope/">
          <Body>
              <${method} xmlns="http://tojEndpoint/">
                ${paramsString}
              </${method}>
          </Body>
      </Envelope>`,
        {
          responseType: 'text',
          headers: new HttpHeaders({
            'Accept': '*/*',
            'Content-Type': 'text/xml; charset=UTF-8'
          })
        }).toPromise();

      let firstObj = data.indexOf('{');
      let firstArray = data.indexOf('[');

      if (firstObj === -1) {
        firstObj = Number.MAX_SAFE_INTEGER;
      }
      if (firstArray === -1) {
        firstArray = Number.MAX_SAFE_INTEGER;
      }

      const isArray = firstObj > firstArray;

      data = data.substr(data.indexOf(isArray ? '[' : '{'));
      data = data.substr(0, data.lastIndexOf(isArray ? ']' : '}') + 1);

      const parse = JSON.parse(BackendService.restoreXml(data));
      console.log('Parsed Response of Method: ' + method);
      return parse;
    } catch (err) {
      // noinspection JSUnusedAssignment
      console.log('Failed to parse: ', data);
      // noinspection JSUnusedAssignment
      throw new Error('Failed to parse (Method = ' + method + '): ' + data);
    } finally {
      if (triggerReload) {
        this.eventQueue.fireEvent('relaod');
      }
    }
  }

  public async updateDbRevision() {
    const newRevision = (await this.sendRequest("dbRevision", {}, false));
    if (this.dbRevision !== newRevision[0]) {
      this.dbRevision = newRevision[0];
      this.eventQueue.fireEvent('relaod');
    }
  }
}
